import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useOutletContext } from 'react-router';
import type { Transition } from 'history';

import { useDispatch, useSelector } from 'hooks';
import useRouterSteps from 'hooks/useRouterSteps';
import useRouterPrompt from 'hooks/useRouterPrompt';
import { logout } from 'modules/auth/actions';
import { shouldDisplayBasketModal } from 'modules/basket/selectors';
import { getSelectedSelfService, getSelfServiceType } from 'modules/selfServices/selectors';
import type PageBaseProps from 'types/PageBase';
import { getBasePath, getStepFromPath } from 'utils/stepUrlUtils';

import PageHeader from 'components/ui/PageHeader';
import type { SelfServiceOutletContext } from './types';

const StepContent: React.FC = () => {
  const {
    steps,
    onStepChange,
  } = useOutletContext<SelfServiceOutletContext>();

  const {
    currentStep,
    next,
    previous,
  } = useRouterSteps<unknown, PageBaseProps>({
    steps,
    onStepChange,
  });
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const selfService = useSelector(getSelectedSelfService);
  const displayBasket = useSelector(shouldDisplayBasketModal);
  const type = useSelector(getSelfServiceType)?.toLowerCase();

  const promptMessage = intl.formatMessage({
    id: `prompt.backPrevent.${type}`,
    defaultMessage: `Warning, you are about to leave your ${type}.`,
  });

  const shouldPrompt = React.useCallback((action: Transition): boolean => {
    const currentBasePath = getBasePath(location.pathname);
    const nextBasePath = getBasePath(action.location.pathname);

    if (currentBasePath === nextBasePath) {
      // Same flow: only block if preventBrowserNavigation and target is the previous step (or unknown)
      const nextStepKey = getStepFromPath(action.location.pathname);
      const nextLocationStep = steps.find(({ key }) => key === nextStepKey);
      return !nextLocationStep?.next || nextLocationStep.next === currentStep.key;
    }
    return true;
  }, [currentStep, location.pathname, steps]);

  const isFirstStep = steps[0].key === currentStep.key;
  const shouldBlock = Boolean(currentStep.data?.preventBrowserNavigation || isFirstStep);
  const onConfirmLeave = React.useCallback(() => dispatch(logout()), [dispatch]);
  useRouterPrompt(promptMessage, shouldBlock, onConfirmLeave, shouldPrompt);

  const visibleSteps = steps.filter(({ data }) => !data?.hideHeader);
  const currentIndex = visibleSteps.findIndex(({ key }) => key === currentStep.key);

  const isPenultimate = React.useMemo(() => {
    const penultimateStepKey = steps[steps.length - 2]?.key;
    return currentStep?.key === penultimateStepKey;
  }, [currentStep?.key, steps]);

  const Component = React.useMemo(
    () => ('render' in currentStep ? currentStep.render : currentStep.component),
    [currentStep]
  );

  const titleMessage = React.useMemo(() => {
    if (currentStep.title) {
      return typeof currentStep.title === 'function'
        ? currentStep.title(selfService)
        : currentStep.title;
    }
    return undefined;
  }, [currentStep, selfService]);

  return (
    <>
      {!currentStep.data?.hideHeader && (
        <PageHeader
          onPrev={previous}
          currentIndex={currentIndex}
          length={visibleSteps.length}
          displayBasket={displayBasket}
          hideSteps={currentStep.data?.hideStepIndicator}
          hidePrevButton={currentStep.data?.hidePrevButton}
          title={titleMessage ? <FormattedMessage {...titleMessage} /> : undefined}
        />
      )}
      <Component
        onNext={next}
        onPrev={previous}
        isPenultimate={isPenultimate}
        shouldDisplayBackButton={currentIndex > 0}
      />
    </>
  );
};

export default StepContent;
